import { gql } from '@apollo/client';
import {
  AlertDescription,
  Box,
  Button,
  chakra,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { chain, isEmpty } from 'lodash';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import BackToAllActionsButton from '~components/displays/BulkAction/BackToAllActionsButton';
import { InfoAlert, WarningAlert } from '~components/ui/Alert';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { ensure } from '~utils/types';
import { useBulkSyncDisplaysMutation } from './__generated__/BulkSyncDisplays.graphql';
import { BulkActionComponent } from './BulkActionModal';

export const BulkSyncDisplays: BulkActionComponent = ({
  displays,
  onCancel,
  onBack,
  onSuccess,
}) => {
  const displaysToSyncByPlaylist = chain(displays)
    .filter((display) => Boolean(display.playlist?.current?.isOutOfSync))
    .map((display) => {
      return {
        playlistId: ensure(display.playlist?.current?.id),
        displayId: display.id,
      };
    })
    .groupBy((d) => d.playlistId)
    .value();

  const isSyncNeeded = !isEmpty(displaysToSyncByPlaylist);
  const { t } = useTranslation();
  const analytics = useAnalyticsReporter();
  const toast = useToast();
  const [bulkSyncDisplays] = useBulkSyncDisplaysMutation();
  const playlistNotSupported = displays.filter((d) => !d.playlist);
  const playlistNotSupportedCount = playlistNotSupported.length;
  const playlistNotSupportedAlias =
    playlistNotSupportedCount === 1 ? playlistNotSupported[0].alias : null;
  const handleBulkSyncDisplays = useCallback(async () => {
    try {
      let totalCount = 0;
      for (const [playlistId, displays] of Object.entries(displaysToSyncByPlaylist)) {
        await bulkSyncDisplays({
          variables: {
            input: {
              playlistId,
              displayIds: displays.map((d) => d.displayId),
            },
          },
        });
        totalCount += displays.length;
      }

      toast({
        status: 'success',
        title: t('displaySyncStarted'),
        description: t('playlistSyncToDisplaysDesc'),
      });

      if (displays.length === 1) {
        analytics.track('playlistUpdate');
      } else {
        analytics.track('displayBulkActionComplete', {
          action: 'updatePlaylist',
          displayCount: totalCount,
        });
      }

      await onSuccess();
    } catch (err) {
      toast({
        status: 'error',
        title: t('displaySyncStartErr'),
        description: fromError(err, 'SyncToDisplays'),
      });
    }
  }, [analytics, bulkSyncDisplays, displaysToSyncByPlaylist, onSuccess, toast, displays.length, t]);

  return (
    <ModalContent>
      <ModalHeader>{t('syncDisplays')}</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      {isSyncNeeded ? (
        <>
          {playlistNotSupportedCount > 0 && (
            <Box px="7" py="8">
              <WarningAlert>
                <AlertDescription>
                  {' '}
                  <Trans
                    i18nKey="playlistNotSupported"
                    count={playlistNotSupportedCount}
                    values={{
                      name: playlistNotSupportedAlias,
                      count: playlistNotSupportedCount,
                    }}
                    components={{
                      strong: <chakra.strong />,
                    }}
                  />
                </AlertDescription>
              </WarningAlert>
            </Box>
          )}
          {playlistNotSupportedCount == 0 && (
            <ModalBody>
              <Text marginTop={4}>{t('playlistSyncAlert')}</Text>
              <InfoAlert mt={3}>{t('playlistSyncInfoAlert')}</InfoAlert>
            </ModalBody>
          )}
          <ModalFooter>
            <HStack flex="1" alignItems="center">
              <BackToAllActionsButton onBack={onBack} />
              <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center">
                <Button variant="ghost" colorScheme="blue" onClick={onCancel}>
                  {t('cancel')}
                </Button>
                <Button
                  marginLeft="3"
                  variant="solid"
                  colorScheme="blue"
                  onClick={handleBulkSyncDisplays}
                  isDisabled={(playlistNotSupportedCount ?? 0) > 0}
                >
                  {t('apply')}
                </Button>
              </Box>
            </HStack>
          </ModalFooter>
        </>
      ) : (
        <>
          {playlistNotSupportedCount > 0 && (
            <Box px="7" py="8">
              <WarningAlert>
                <AlertDescription>
                  {' '}
                  <Trans
                    i18nKey="playlistNotSupported"
                    count={playlistNotSupportedCount}
                    values={{
                      name: playlistNotSupportedAlias,
                      count: playlistNotSupportedCount,
                    }}
                    components={{
                      strong: <chakra.strong />,
                    }}
                  />
                </AlertDescription>
              </WarningAlert>
            </Box>
          )}
          {playlistNotSupportedCount == 0 && (
            <ModalBody>
              <Stack spacing="4">
                <InfoAlert>{t('playlistInSyncAlert')}</InfoAlert>
              </Stack>
            </ModalBody>
          )}
          <ModalFooter>
            <HStack flex="1" alignItems="center">
              <BackToAllActionsButton onBack={onBack} />
              <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  marginLeft="3"
                  variant="solid"
                  colorScheme="blue"
                  onClick={onCancel}
                  isDisabled={(playlistNotSupportedCount ?? 0) > 0}
                >
                  {t('ok')}
                </Button>
              </Box>
            </HStack>
          </ModalFooter>
        </>
      )}
    </ModalContent>
  );
};

BulkSyncDisplays.graphql = {
  fragments: {
    BulkSyncDisplays_display: gql`
      fragment BulkSyncDisplays_display on Display {
        id
        playlist {
          current {
            id
            isOutOfSync
          }
        }
      }
    `,
  },
  mutations: {
    BulkSyncDisplays: gql`
      mutation BulkSyncDisplays($input: DisplayBulkUpdatePlaylistInput!) {
        displayBulkUpdatePlaylist(input: $input) {
          displays {
            id
          }
        }
      }
    `,
  },
};
